import React, { FormEvent } from 'react';
import { navigate } from 'gatsby';
import { FormProvider, useForm } from 'react-hook-form';
import styled from '@emotion/styled';

import Grid from '@/components/atoms/Grid';
import Input from '@/components/atoms/Form/Input';
import Textarea from '@/components/atoms/Form/Textarea/Textarea';
import Form from '@/components/atoms/Form/Form';
import Button from '@/components/atoms/Button';
import FormError from '@/components/atoms/Form/FormError';
import Select from '@/components/atoms/Form/Select';

import formValidationRules from '@/utils/formValidationRules';
import renderFormErrorMessage from '@/utils/renderFormErrorMessage';
import mq from '@/styles/mq';

interface Props {
  form_name: string;
}

const FieldGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
const TwoColumnsGrid = styled(FieldGrid)`
  flex-direction: column;

  & > *:first-of-type {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }

  ${mq('lg')} {
    flex-direction: row;

    & > *:first-of-type {
      margin-bottom: 0;
      padding-right: ${({ theme }) => theme.spacing(3)};
    }

    & > *:last-of-type {
      padding-left: ${({ theme }) => theme.spacing(3)};
    }
  }
`;
const Submit = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-left: auto;
  margin-right: auto;
`;
const InputContainer = styled('div')`
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing(3)};
`;
const FormErrorStyled = styled(FormError)`
  position: absolute;
  bottom: 0;
`;

const encode = (data) => {
  return Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
};

const ContactForm = ({ form_name }: Props): JSX.Element => {
  const methods = useForm();
  const { register, reset, handleSubmit, setError, errors } = methods;

  const inputTextValidation = formValidationRules('text', true);
  const emailValidation = formValidationRules('email', true);
  const phoneValidation = formValidationRules('tel', true);
  const textareaValidation = formValidationRules('textarea', true);

  const onSubmit = async (data: FormData) => {
    try {
      await fetch('/', {
        method: `POST`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: encode({
          ...data,
          'form-name': `contact-form-${form_name}`,
        }),
      });

      navigate(`/${form_name}/contact/merci`);
      reset();
      setTimeout(() => navigate(`/${form_name}/contact`), 4000);
    } catch (e) {
      reset();
    }
  };

  const onInputChange = (
    e: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    // @ts-ignore
    setError([e.target.name], {});
  };
  return (
    <FormProvider {...methods}>
      <Form
        name={`contact-form-${form_name}`}
        onSubmit={handleSubmit(onSubmit)}
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
      >
        <input type="hidden" name="bot-field" />
        <input
          type="hidden"
          name="form-name"
          value={`contact-form-${form_name}`}
          ref={register({ required: true })}
        />
        <FieldGrid>
          <InputContainer>
            <Select
              label="Vous êtes interessés par"
              name="interest"
              ref={register({ required: true })}
            >
              <option value="">Sélectionnez</option>
              <option value="Usine">L'usine</option>
              <option value="Magasin">Le magasin</option>
            </Select>
            {errors.interest && (
              <FormErrorStyled>
                {renderFormErrorMessage(errors?.interest?.type)}
              </FormErrorStyled>
            )}
          </InputContainer>
        </FieldGrid>
        <TwoColumnsGrid container>
          <Grid xxs={12} lg={6}>
            <InputContainer>
              <Input
                name="firstname"
                label="Prénom"
                onChange={onInputChange}
                ref={register(inputTextValidation)}
              />
              {errors.firstname && (
                <FormErrorStyled>
                  {renderFormErrorMessage(errors?.firstname?.type)}
                </FormErrorStyled>
              )}
            </InputContainer>
          </Grid>
          <Grid xxs={12} lg={6}>
            <InputContainer>
              <Input
                name="lastname"
                label="Nom"
                onChange={onInputChange}
                ref={register(inputTextValidation)}
              />
              {errors.lastname && (
                <FormErrorStyled>
                  {renderFormErrorMessage(errors?.lastname?.type)}
                </FormErrorStyled>
              )}
            </InputContainer>
          </Grid>
        </TwoColumnsGrid>
        <TwoColumnsGrid container>
          <Grid xxs={12} lg={6}>
            <InputContainer>
              <Input
                name="email"
                label="Adresse e-mail"
                onChange={onInputChange}
                ref={register(emailValidation)}
              />
              {errors.email && (
                <FormErrorStyled>
                  {renderFormErrorMessage(errors?.email?.type, 'email')}
                </FormErrorStyled>
              )}
            </InputContainer>
          </Grid>
          <Grid xxs={12} lg={6}>
            <InputContainer>
              <Input
                name="phone"
                label="Numéro de téléphone"
                onChange={onInputChange}
                ref={register(phoneValidation)}
              />
              {errors.phone && (
                <FormErrorStyled>
                  {renderFormErrorMessage(errors?.phone?.type, 'tel')}
                </FormErrorStyled>
              )}
            </InputContainer>
          </Grid>
        </TwoColumnsGrid>
        <FieldGrid>
          <InputContainer>
            <Textarea
              name="message"
              label="Votre message"
              onChange={onInputChange}
              ref={register(textareaValidation)}
            />
            {errors.message && (
              <FormErrorStyled>
                {renderFormErrorMessage(errors?.message?.type)}
              </FormErrorStyled>
            )}
          </InputContainer>
        </FieldGrid>
        <Grid container>
          <Submit type="submit">Envoyer</Submit>
        </Grid>
      </Form>
    </FormProvider>
  );
};

export default ContactForm;
